import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'react-native-modalfy';

import { LanguageCodesEnum } from '@constants';
import { IconTouchable } from '../../common/IconTouchable';
import { IS_MOBILE } from '@utils';
import { modals, ModalStackParamsType } from '@modals';
import { usePopover } from '@hooks';
import { PickerPopover } from '../PickerPopover';
import { useVideoPlayerSettings } from './hooks/useVideoPlayerSettings';

export type SwitchSubtitlesLanguageType = null | LanguageCodesEnum;

type OptionType = {
  title: string;
  value: null | LanguageCodesEnum;
};

export interface ISubtitlesMenuProps {
  onVisibleChange?(value: boolean): void;
}

export const SubtitlesMenu = observer<ISubtitlesMenuProps>(
  ({ onVisibleChange }) => {
    const videoPlayerSettings = useVideoPlayerSettings();
    const { t } = useTranslation();
    const modal = useModal<ModalStackParamsType>();
    const popover = usePopover();

    const options = useMemo(() => {
      const offOption: OptionType[] = [
        { title: t('common.videoPlayer.off'), value: null },
      ];

      return offOption.concat(
        Object.values(LanguageCodesEnum).map((item) => ({
          value: item,
          title: t(`common.languages.${item}`),
        })),
      );
    }, []);

    function switchSubtitles(item: OptionType) {
      popover.togglePopover();
      videoPlayerSettings.setSubtitlesLanguage(item.value);
    }

    function openSubtitlesModal() {
      modal.openModal(modals.PickerModal, {
        selectedValue: videoPlayerSettings.subtitlesLanguage,
        onSelect: (item) =>
          videoPlayerSettings.setSubtitlesLanguage(item.value),
        options,
      });
    }

    if (IS_MOBILE) {
      return (
        <IconTouchable
          name="subtittle-bold"
          color="$background0"
          onPress={openSubtitlesModal}
        />
      );
    }

    return (
      <PickerPopover
        ref={popover.ref}
        options={options}
        onSelect={switchSubtitles}
        onVisibleChange={onVisibleChange}
        selectedValue={videoPlayerSettings.subtitlesLanguage}
      >
        <IconTouchable
          name="subtittle-bold"
          color="$background0"
          onPress={popover.togglePopover}
        />
      </PickerPopover>
    );
  },
);
